import React from 'react';
import { ShieldIcon, HomeIcon, HeartPulseIcon, BriefcaseIcon, PhoneIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface VerzekeringenPageProps {
  onNavigate: (page: string) => void;
}
export function VerzekeringenPage({
  onNavigate
}: VerzekeringenPageProps) {
  return <div className="w-full" data-id="element-791">
      {/* Hero */}
      <PageHero title="Zekerheid voor wat echt telt" subtitle="Geen onnodige polissen of kleine lettertjes. Wij zorgen dat je goed verzekerd bent voor wat er echt toe doet. Gebeurt er iets? Dan bel je ons." label="Verzekeringen" data-id="element-792">
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-793">
          <Button variant="secondary" size="lg" onClick={() => onNavigate('contact')} data-id="element-794">
            Verzekeringen doorspreken
          </Button>
          <Button variant="outline" size="lg" href="tel:0232052161" icon={<PhoneIcon className="w-5 h-5" data-id="element-796" />} iconPosition="left" data-id="element-795">
            Schade melden
          </Button>
        </div>
      </PageHero>

      {/* Hoe werkt schade melden? */}
      <section className="py-20 md:py-28 bg-white" data-id="element-797">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-798">
          <SectionHeading title="Hoe werkt schade melden?" data-id="element-799" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-800">
            <FadeIn delay={0.1} className="text-center" data-id="element-801">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-802">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-803">
                Bel ons direct
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-804">
                Je krijgt direct je eigen adviseur aan de lijn. Geen keuzemenu's
                of wachttijden.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="text-center" data-id="element-805">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-806">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-807">
                Wij regelen het
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-808">
                Stuur ons foto's via WhatsApp. Wij nemen contact op met de
                verzekeraar en regelen de afhandeling.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="text-center" data-id="element-809">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-810">
                3
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-811">
                Snel opgelost
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-812">
                Wij zorgen voor een snelle uitkering of reparatie, zodat jij
                weer verder kunt.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Insurance Grid */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-813">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-814">
          <SectionHeading title="Verzekeringen" data-id="element-815" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-816">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-817">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-818">
                <HomeIcon className="w-7 h-7 text-gold" data-id="element-819" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-820">Wonen</h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-821">
                Opstal- en inboedelverzekering perfect afgestemd op jouw
                (nieuwe) woning of woonboot.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-822">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-823">
                <HeartPulseIcon className="w-7 h-7 text-gold" data-id="element-824" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-825">
                Overlijdensrisico
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-826">
                Financiële rust voor je nabestaanden. Zorg dat de hypotheek
                betaalbaar blijft als jij of je partner wegvalt.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-827">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-828">
                <BriefcaseIcon className="w-7 h-7 text-gold" data-id="element-829" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-830">
                Inkomensbescherming
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-831">
                Vang de financiële klap op bij arbeidsongeschiktheid of
                werkloosheid. Cruciaal voor ondernemers en huiseigenaren.
              </p>
            </FadeIn>
          </div>

          <FadeIn delay={0.6} className="mt-12 text-center" data-id="element-832">
            <p className="text-gray-500 text-sm max-w-2xl mx-auto" data-id="element-833">
              Wij adviseren alleen over verzekeringen die echt relevant zijn
              voor jouw hypotheek en financiële situatie.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Trust Quote */}
      <section className="py-20 md:py-28 bg-white" data-id="element-834">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-835">
          <FadeIn className="bg-warm p-8 md:p-10 rounded-2xl border border-gray-100 shadow-card text-center" data-id="element-836">
            <div className="flex justify-center gap-1 mb-4" data-id="element-837">
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-838" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-839" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-840" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-841" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-842" />
            </div>
            <p className="text-gray-700 italic text-lg leading-relaxed mb-4" data-id="element-843">
              "Na een waterschade was alles binnen een week geregeld. Eén
              telefoontje en JDG nam het volledig over. Dat is pas service."
            </p>
            <p className="font-bold text-navy" data-id="element-844">Familie Bakker</p>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <CTASection title="Op zoek naar hypotheekadvies?" subtitle="De verzekeringen sluiten naadloos aan op jouw hypotheek. Bekijk ook het hypotheekadvies." buttonText="Bekijk hypotheekadvies" buttonVariant="secondary" onNavigate={onNavigate} page="hypotheken" variant="light" data-id="element-845" />
    </div>;
}